class FragranceSearch extends HTMLElement {
  constructor() {
    super();
    this.searchInput = this.querySelector('[data-fragrance-search-input]');
    this.dropdown = this.querySelector('[data-fragrance-dropdown]');
    this.resultsContainer = this.querySelector('[data-fragrance-results]');
    this.recommendationsSection = this.querySelector('[data-recommendations-section]');
    this.recommendationsGrid = this.querySelector('[data-recommendations-grid]');
    this.familiesSection = this.querySelector('[data-families-section]');
    this.familiesGrid = this.querySelector('[data-families-grid]');
    this.notesGrid = this.querySelector('[data-notes-grid]');
    this.notesGridTitle = this.querySelector('[data-notes-title]');
    this.selectedFragranceName = this.querySelector('[data-selected-fragrance]');
    this.loadingOverlay = this.querySelector('[data-loading-overlay]');

    this.debounceTimer = null;
    this.collectedFamilies = new Set();
    this.collectedNotes = [];
    this.pendingImageLoads = 0;

    // Load family data from JSON script tag
    this.familyData = this.loadFamilyData();

    // Family descriptions mapping (fallback)
    this.familyDescriptions = {
      'Fresh': 'Light, clean, invigorating scents',
      'Floral': 'Romantic, feminine, garden-inspired',
      'Oriental': 'Exotic, warm, spicy compositions',
      'Woody': 'Earthy, sophisticated, grounding',
      'Citrus': 'Bright, energizing, zesty notes',
      'Aquatic': 'Marine, oceanic, watery freshness',
      'Fruity': 'Sweet, juicy, playful aromas',
      'Green': 'Leafy, crisp, natural essences',
      'Spicy': 'Warm, intense, peppery blends',
      'Aromatic': 'Herbal, Mediterranean, lavender-like'
    };
    
    this.init();
  }

  loadFamilyData() {
    try {
      const dataScript = document.querySelector('[data-fragrance-search-data]');
      if (dataScript) {
        return JSON.parse(dataScript.textContent);
      }
    } catch (error) {
      console.error('Error loading family data:', error);
    }
    return {};
  }

  init() {
    this.searchInput.addEventListener('input', this.handleSearch.bind(this));
    this.searchInput.addEventListener('focus', this.showDropdown.bind(this));
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  handleSearch(e) {
    const query = e.target.value.trim();

    clearTimeout(this.debounceTimer);
    
    if (query.length < 2) {
      this.hideDropdown();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.searchFragrances(query);
    }, 300);
  }

  async searchFragrances(query) {
    try {
      const proxyUrl = `https://libertine-perfumerie-fotw.vercel.app/api/fotw-proxy?endpoint=fragrances&q=${encodeURIComponent(query)}`;

      const response = await fetch(proxyUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate that we received an array of fragrances
      if (!data || !Array.isArray(data)) {
        console.warn('Invalid response format from fragrances API:', data);
        this.hideDropdown();
        return;
      }

      this.displayResults(data);
    } catch (error) {
      console.error('Error searching fragrances:', error);
      this.hideDropdown();
    }
  }

  displayResults(fragrances) {
    // Additional validation to ensure fragrances is an array
    if (!fragrances || !Array.isArray(fragrances) || fragrances.length === 0) {
      console.warn('displayResults called with invalid data:', fragrances);
      this.hideDropdown();
      return;
    }

    try {
      this.resultsContainer.innerHTML = fragrances.map(fragrance => {
        // Validate each fragrance object has required properties
        if (!fragrance || !fragrance.ID || !fragrance.FragranceName) {
          console.warn('Invalid fragrance object:', fragrance);
          return '';
        }

        const fragranceName = fragrance.FragranceName.toLowerCase();
        return `
          <li class="fragrance-search__result-item" data-fragrance-id="${fragrance.ID}">
            <span class="fragrance-search__result-name h3">${fragranceName}</span>
            <span class="fragrance-search__result-brand h6 text-uppercase">${fragrance.Brand || 'Unknown Brand'}</span>
          </li>
        `;
      }).filter(html => html !== '').join('');

      this.resultsContainer.addEventListener('click', this.handleResultClick.bind(this));
      this.showDropdown();
    } catch (error) {
      console.error('Error displaying search results:', error);
      this.hideDropdown();
    }
  }

  async handleResultClick(e) {
    const resultItem = e.target.closest('.fragrance-search__result-item');
    if (!resultItem) return;

    const fragranceId = resultItem.dataset.fragranceId;
    const fragranceName = resultItem.querySelector('.fragrance-search__result-name').textContent;

    this.searchInput.value = fragranceName;
    this.selectedFragranceName.textContent = fragranceName.toLowerCase();
    this.hideDropdown();

    // Store scroll position before showing loading
    const contentWrapper = this.querySelector('[data-content-wrapper]');
    const scrollTarget = contentWrapper.offsetTop;

    // Scroll to content wrapper
    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth'
    });

    // Show loading overlay after a brief delay to allow scroll to start
    setTimeout(() => {
      this.showLoadingOverlay();
    }, 100);

    await this.loadRecommendations(fragranceId);
  }

  showLoadingOverlay() {
    if (this.loadingOverlay) {
      const contentWrapper = this.querySelector('[data-content-wrapper]');
      if (contentWrapper) {
        contentWrapper.classList.add('fragrance-search__content-wrapper--loading');
      }
      this.loadingOverlay.classList.add('fragrance-search__loading-overlay--visible');
    }
  }

  hideLoadingOverlay() {
    if (this.loadingOverlay) {
      const contentWrapper = this.querySelector('[data-content-wrapper]');
      if (contentWrapper) {
        contentWrapper.classList.remove('fragrance-search__content-wrapper--loading');
      }
      this.loadingOverlay.classList.remove('fragrance-search__loading-overlay--visible');
    }
  }

  async loadRecommendations(fragranceId) {
    try {
      const proxyUrl = `https://libertine-perfumerie-fotw.vercel.app/api/fotw-proxy?endpoint=recommendations&id=${fragranceId}`;

      const response = await fetch(proxyUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate that we received an array of recommendations
      if (!data || !Array.isArray(data)) {
        console.warn('Invalid recommendations response format:', data);
        return;
      }

      this.collectedFamilies.clear();
      this.collectedNotes = [];

      this.displayRecommendations(data);
      this.collectFamilies(data);

      // Load fragrance details to get actual notes
      await this.loadAndCollectFragranceNotes(fragranceId);

      this.displayFamiliesAndNotes();
        
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  }

  async loadFragranceDetails(fragranceId) {
    try {
      const proxyUrl = `https://libertine-perfumerie-fotw.vercel.app/api/fotw-proxy?endpoint=details&id=${fragranceId}`;

      const response = await fetch(proxyUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const details = await response.json();

      console.log('Fragrance details:', details);

      return details;
    } catch (error) {
      console.error('Error loading fragrance details:', error);
      throw error;
    }
  }

  async loadAndCollectFragranceNotes(fragranceId) {
    try {
      const details = await this.loadFragranceDetails(fragranceId);

      // Extract notes from consolidated ingredients
      if (details && details.notes) {
        this.collectedNotes = details.notes;
        console.log('Collected notes from fragrance details:', this.collectedNotes);
      }
    } catch (error) {
      console.error('Error loading and collecting fragrance notes:', error);
    }
  }

  async displayRecommendations(recommendations) {
    if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
      console.warn('displayRecommendations called with invalid data:', recommendations);
      this.hideLoadingOverlay();
      return;
    }

    const limitedRecommendations = recommendations.slice(0, 3);

    // Create placeholder cards first
    this.recommendationsGrid.innerHTML = limitedRecommendations.map((rec, index) => `
      <div class="fragrance-search__recommendation-placeholder" data-rec-index="${index}">
      <div class="loading-placeholder"><div class="h3">Loading Recommendation</div></div>

      </div>
    `).join('');

    // Update grid layout based on number of recommendations
    this.recommendationsSection.style.display = 'block';

    // Track number of products to load
    this.pendingImageLoads = limitedRecommendations.length;

    // Fetch and render product cards for each recommendation
    limitedRecommendations.forEach(async (rec, index) => {
      if (rec.MerchantFragranceDetails?.link) {
        try {
          const productData = await this.fetchProductData(rec.MerchantFragranceDetails.link);
          await this.renderProductCard(index, productData.product, rec);
        } catch (error) {
          console.error(`Error fetching product data for ${rec.FragranceName}:`, error);
          this.renderFallbackCard(index, rec);
          this.decrementPendingLoads();
        }
      } else {
        this.renderFallbackCard(index, rec);
        this.decrementPendingLoads();
      }
    });
  }

  decrementPendingLoads() {
    this.pendingImageLoads--;
    if (this.pendingImageLoads <= 0) {
      this.hideLoadingOverlay();
    }
  }

  async fetchProductData(productLink) {
    try {
      // Add view=json parameter to the product link
      const jsonUrl = `${productLink}&view=json`;
      const response = await fetch(jsonUrl);
      const productData = await response.json();
      return productData;
    } catch (error) {
      console.error('Error fetching product JSON:', error);
      throw error;
    }
  }

  async renderProductCard(index, product, recommendation) {
    const placeholder = this.recommendationsGrid.querySelector(`[data-rec-index="${index}"]`);
    if (!placeholder || !product) {
      this.decrementPendingLoads();
      return;
    }

    try {
      // Fetch product card HTML from Shopify
      const productCardHtml = await this.fetchProductCardSnippet(product.handle);

      // Check if placeholder still has a parent before replacing
      if (placeholder.parentNode) {
        // Use innerHTML to avoid parent node issues
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productCardHtml;
        const newElement = tempDiv.firstElementChild;

        if (newElement) {
          // Find the main product image
          const productImage = newElement.querySelector('img');

          if (productImage) {
            // Wait for the image to load before replacing placeholder
            if (productImage.complete) {
              // Image already loaded
              placeholder.parentNode.replaceChild(newElement, placeholder);
              this.initializeInjectedProductCards();
              this.decrementPendingLoads();
            } else {
              // Wait for image to load
              productImage.addEventListener('load', () => {
                if (placeholder.parentNode) {
                  placeholder.parentNode.replaceChild(newElement, placeholder);
                  this.initializeInjectedProductCards();
                }
                this.decrementPendingLoads();
              });
              productImage.addEventListener('error', () => {
                if (placeholder.parentNode) {
                  placeholder.parentNode.replaceChild(newElement, placeholder);
                  this.initializeInjectedProductCards();
                }
                this.decrementPendingLoads();
              });
            }
          } else {
            // No image found, replace immediately
            placeholder.parentNode.replaceChild(newElement, placeholder);
            this.initializeInjectedProductCards();
            this.decrementPendingLoads();
          }
        } else {
          console.error(`Invalid HTML returned for ${product.handle}`);
          this.renderFallbackCard(index, recommendation);
          this.decrementPendingLoads();
        }
      } else {
        console.warn(`Placeholder for ${product.handle} was removed from DOM before rendering`);
        this.renderFallbackCard(index, recommendation);
        this.decrementPendingLoads();
      }
    } catch (error) {
      console.error(`Error rendering product card for ${product.handle}:`, error);
      this.renderFallbackCard(index, recommendation);
      this.decrementPendingLoads();
    }
  }

  async fetchProductCardSnippet(productHandle) {
    try {
      // Use Shopify's product card snippet rendering
      const response = await fetch(`/products/${productHandle}?view=card`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product card: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error fetching product card snippet:', error);
      throw error;
    }
  }

  renderFallbackCard(index, recommendation) {
    const placeholder = this.recommendationsGrid.querySelector(`[data-rec-index="${index}"]`);
    if (!placeholder) return;

    placeholder.innerHTML = `
      <div class="fragrance-search__fallback-card">
        <div class="fragrance-search__fallback-image">
          <div class="fragrance-search__fallback-placeholder"></div>
        </div>
        <h5 class="fragrance-search__fallback-name">${recommendation.FragranceName}</h5>
        <p class="fragrance-search__fallback-brand">${recommendation.Brand}</p>
        <p class="fragrance-search__fallback-note">Product not available in store</p>
      </div>
    `;
    placeholder.style.display = 'none';
  }

  collectFamilies(recommendations) {
    if (!recommendations || !Array.isArray(recommendations)) {
      console.warn('collectFamilies called with invalid data:', recommendations);
      return;
    }

    recommendations.forEach(rec => {
      if (rec && rec.Family) {
        this.collectedFamilies.add(rec.Family);
      }
    });
  }

  displayFamiliesAndNotes() {
    // Display families with circles and descriptions
    if (this.collectedFamilies.size > 0) {
      this.familiesGrid.innerHTML = Array.from(this.collectedFamilies).map(family => {
        // Get family data from the loaded JSON or fallback to hardcoded descriptions
        const familyInfo = this.familyData[family];
        const description = familyInfo?.description || this.familyDescriptions[family] || 'Explore this unique fragrance family';
        const color = familyInfo?.color || '#666';
        const image = familyInfo?.image;
        // handleize the family name for URL
        const familyHandle = family.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        const familyLink = `/collections/${familyHandle}`;

        // Set the wheel value and show container, but don't spin yet
        if (window.setWheelValue) {
          window.setWheelValue(familyHandle);
          let wheelContainer = document.querySelector('.wheel-outer-container');
          if (wheelContainer) {
            wheelContainer.classList.remove('hidden');
          }
        }

        return `
          <div class="fragrance-search__family-item">
            <div class="fragrance-search__family-circle" style="background-color: ${color}">
              ${image ? `<img src="${image}" alt="${family}" class="fragrance-search__family-image">` : ''}
              <span class="fragrance-search__family-name">${family}</span>
            </div>
            <p class="fragrance-search__family-description">${description}</p>
            <a href="${familyLink}" class="fragrance-search__discover-link" data-family="${family}">Discover Family</a>
          </div>
        `;
      }).join('');

      // Add click handlers for discover links
      this.familiesGrid.querySelectorAll('.fragrance-search__discover-link').forEach(link => {
        link.addEventListener('click', (e) => {
          // e.preventDefault();
          const family = e.target.dataset.family;
          console.log(`Discovering family: ${family}`);
          // Add your discovery logic here
        });
      });
    }

    // Display notes  
    if (this.collectedNotes.length > 0) {
      this.notesGrid.innerHTML = this.collectedNotes.map(note => `
        <a class="fragrance-search__note-tag" href="/collections/all?filter.p.m.custom.fragrance_notes=${encodeURIComponent(note.ingredient_name)}">
          <img src="${note.ingredient_image_url}" alt="${note.ingredient_name}" class="fragrance-search__note-image">
          <span>${note.ingredient_name}</span>
        </a>
      `).join('');
      this.notesGridTitle.style.display = 'block';
    } else {
      this.notesGridTitle.style.display = 'none';
    }

    if (this.collectedFamilies.size > 0 || this.collectedNotes.length > 0) {
      this.familiesSection.style.display = 'block';
    }
  }

  showDropdown() {
    this.dropdown.classList.add('fragrance-search__dropdown--visible');
  }

  hideDropdown() {
    this.dropdown.classList.remove('fragrance-search__dropdown--visible');
  }

  handleOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.hideDropdown();
    }
  }

  // Initialize product cards after they're injected from the recommendations
  initializeInjectedProductCards() {
    // Find newly injected product cards in the recommendations grid
    const productCards = this.recommendationsGrid.querySelectorAll('product-card, .product-card--root');

    productCards.forEach(card => {
      const options = card.querySelectorAll('.product-options--option');
      const addButton = card.querySelector('button.add[data-variant]');

      if (!addButton) return;

      // Handle option changes with custom fragrance search logic
      options.forEach(optionContainer => {
        const radioButtons = optionContainer.querySelectorAll('.radios--value-button');

        radioButtons.forEach(button => {
          // Remove any existing fragrance search listeners
          button.removeEventListener('click', button._fragranceSearchHandler);

          // Create a custom handler for this button
          button._fragranceSearchHandler = (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Handle the radio selection manually
            const radio = button.querySelector('input[type="radio"]');
            if (radio && !radio.checked) {
              // Uncheck other radios in the same group
              const radioGroup = optionContainer.querySelectorAll('input[type="radio"]');
              radioGroup.forEach(r => {
                r.checked = false;
                const btn = r.closest('.radios--value-button');
                if (btn) {
                  btn.classList.remove('checked');
                }
              });

              // Check this radio and update visual state
              radio.checked = true;
              button.classList.add('checked');

              // Update the variant
              this.updateFragranceSearchVariant(card);
            }
          };

          // Add the click handler to the button container
          button.addEventListener('click', button._fragranceSearchHandler);
        });
      });

      // Handle add to cart button clicks
      addButton.removeEventListener('click', this.handleAddToCart);
      addButton.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }

  // Update variant for fragrance search with custom logic
  updateFragranceSearchVariant(card) {
    const options = card.querySelectorAll('.product-options--option');
    const addButton = card.querySelector('button.add[data-variant]');
    const priceElement = card.querySelector('button.add .price');

    if (!addButton) return;

    // Find selected options
    const selectedOptions = [];
    options.forEach(optionContainer => {
      const checkedRadio = optionContainer.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        const variantId = checkedRadio.getAttribute('data-option-value-id');
        const price = checkedRadio.getAttribute('data-price');
        selectedOptions.push({ variantId, price });
      }
    });

    // Update button and price
    if (selectedOptions.length > 0) {
      const lastSelected = selectedOptions[selectedOptions.length - 1];
      if (lastSelected.variantId) {
        console.log('Updating button data-variant from', addButton.getAttribute('data-variant'), 'to', lastSelected.variantId);
        addButton.setAttribute('data-variant', lastSelected.variantId);
        addButton.disabled = false;

        if (priceElement && lastSelected.price) {
          priceElement.textContent = lastSelected.price;
          console.log('Updated price to:', lastSelected.price);
        }

        // Update button text
        const buttonTextNode = addButton.childNodes[0];
        if (buttonTextNode && buttonTextNode.nodeType === Node.TEXT_NODE) {
          buttonTextNode.textContent = 'Add to Cart - ';
        }
      }
    }
  }

  // Handle add to cart using theme's cart system
  async handleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target.closest('button.add');
    const variantId = button.getAttribute('data-variant');

    // Make sure we have a variant ID
    if (!variantId) {
      console.error('No variant ID found on add to cart button');
      return;
    }

    // Set button to loading state
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      // Use theme's Cart.addItems static method
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', Cart.sections.join(','));

      const requestOptions = {
        method: 'POST',
        body: formData
      };

      // Use the Cart class static method and open drawer
      await Cart.addItems(requestOptions, button);

    } catch (error) {
      console.error('Error adding to cart:', error);
      // Remove loading state on error
      button.setAttribute('aria-busy', 'false');
      button.disabled = false;
    }
  }

}

customElements.define('fragrance-search', FragranceSearch);